<template>
  <div>
    <!-- =========================================================
         MAIN FLOATING HELP CENTER
    ========================================================== -->
    <v-dialog
      v-model="localVisible"
      class="help-center-dialog"
      hide-overlay
      persistent
      scroll-strategy="none"
      :style="{
        position: 'fixed',
        top: position.top + 'px',
        left: position.left + 'px',
      }"
      style="z-index: 999999"
      width="760"
      max-width="calc(100vw - 32px)"
    >
      <v-card
        ref="dialog"
        class="help-center-card"
        @mousedown.stop
      >
        <!-- =====================================================
             HEADER / DRAG HANDLE
        ====================================================== -->
        <div
          class="help-header cursor-move"
          @mousedown.prevent="startDrag"
        >
          <div class="help-brand">
            <div class="help-brand-icon">
              <v-icon size="25">mdi-lifebuoy</v-icon>
            </div>

            <div>
              <div class="help-title">
                {{ isFrench ? "Centre d'aide" : "Help Center" }}
              </div>

              <div class="help-subtitle">
                {{
                  isFrench
                    ? "Trouvez des réponses ou envoyez-nous un message"
                    : "Find answers or send us a message"
                }}
              </div>
            </div>
          </div>

          <div class="header-actions">
            <v-btn
              icon
              variant="text"
              size="small"
              class="header-btn"
              @mousedown.stop
              @click.stop="copyCurrentContent"
            >
              <v-icon size="20">mdi-content-copy</v-icon>

              <v-tooltip activator="parent" location="bottom">
                {{ isFrench ? "Copier" : "Copy" }}
              </v-tooltip>
            </v-btn>

            <v-btn
              icon
              variant="text"
              size="small"
              class="header-btn"
              @mousedown.stop
              @click.stop="printCurrentContent"
            >
              <v-icon size="20">mdi-printer-outline</v-icon>

              <v-tooltip activator="parent" location="bottom">
                {{ isFrench ? "Imprimer" : "Print" }}
              </v-tooltip>
            </v-btn>

            <v-btn
              icon
              variant="text"
              size="small"
              class="header-btn"
              @mousedown.stop
              @click.stop="close"
            >
              <v-icon size="22">mdi-close</v-icon>
            </v-btn>
          </div>
        </div>

        <!-- =====================================================
             TAB NAVIGATION
        ====================================================== -->
        <div class="help-tabs">
          <button
            type="button"
            class="help-tab"
            :class="{ active: activeBox === 'A' }"
            @click="activeBox = 'A'"
          >
            <v-icon size="19">mdi-creation</v-icon>

            <span>
              {{ isFrench ? "Aide intelligente" : "Smart Help" }}
            </span>
          </button>

          <button
            type="button"
            class="help-tab"
            :class="{ active: activeBox === 'B' }"
            @click="activeBox = 'B'"
          >
            <v-icon size="19">mdi-message-text-outline</v-icon>

            <span>
              {{ isFrench ? "Nous contacter" : "Message Us" }}
            </span>
          </button>
        </div>

        <!-- =====================================================
             SMART FAQ TAB
        ====================================================== -->
        <div
          v-if="activeBox === 'A'"
          class="help-body smart-help-body"
        >
          <!-- SEARCH -->
          <div class="search-area">
            <div class="search-heading">
              {{
                isFrench
                  ? "Comment pouvons-nous vous aider ?"
                  : "How can we help?"
              }}
            </div>

            <div class="search-description">
              {{
                isFrench
                  ? "Posez une question ou recherchez un sujet dans notre centre d'aide."
                  : "Ask a question or search for a topic in our help center."
              }}
            </div>

            <v-text-field
              v-model="input"
              class="smart-search"
              variant="outlined"
              density="comfortable"
              hide-details
              clearable
              autocomplete="off"
              prepend-inner-icon="mdi-magnify"
              :placeholder="
                isFrench
                  ? 'Ex. Comment connecter mon domaine ?'
                  : 'e.g. How do I connect my domain?'
              "
              @keydown.enter.prevent="performSearch"
              @click:clear="clearSearch"
            >
              <template #append-inner>
                <v-progress-circular
                  v-if="loading"
                  indeterminate
                  size="21"
                  width="2"
                />

                <v-btn
                  v-else
                  icon
                  variant="flat"
                  size="small"
                  class="search-submit-btn"
                  :disabled="!input.trim()"
                  @click.stop="performSearch"
                >
                  <v-icon size="19">
                    mdi-arrow-up
                  </v-icon>
                </v-btn>
              </template>
            </v-text-field>

            <div class="search-hint">
              <v-icon size="14">mdi-lightbulb-outline</v-icon>

              <span>
                {{
                  isFrench
                    ? "Essayez des mots comme domaine, hébergement, DNS ou facture."
                    : "Try words such as domain, hosting, DNS, or invoice."
                }}
              </span>
            </div>
          </div>

          <!-- =================================================
               INITIAL STATE
          ================================================== -->
          <div
            v-if="!hasSearched && !loading"
            class="initial-state"
          >
            <div class="initial-icon">
              <v-icon size="34">
                mdi-text-search
              </v-icon>
            </div>

            <div class="initial-title">
              {{
                isFrench
                  ? "Recherchez notre centre d'aide"
                  : "Search our Help Center"
              }}
            </div>

            <div class="initial-text">
              {{
                isFrench
                  ? "Nous rechercherons les questions les plus proches de votre demande."
                  : "We'll find the questions that most closely match what you're looking for."
              }}
            </div>
          </div>

          <!-- =================================================
               LOADING
          ================================================== -->
          <div
            v-if="loading"
            class="search-loading"
          >
            <v-progress-circular
              indeterminate
              size="30"
              width="3"
            />

            <span>
              {{
                isFrench
                  ? "Recherche des réponses..."
                  : "Finding answers..."
              }}
            </span>
          </div>

          <!-- =================================================
               ERROR
          ================================================== -->
          <div
            v-if="searchError && !loading"
            class="state-message error-state"
          >
            <div class="state-icon">
              <v-icon size="28">
                mdi-alert-circle-outline
              </v-icon>
            </div>

            <div>
              <div class="state-title">
                {{
                  isFrench
                    ? "La recherche n'a pas pu être effectuée"
                    : "We couldn't complete the search"
                }}
              </div>

              <div class="state-description">
                {{
                  isFrench
                    ? "Veuillez réessayer dans quelques instants."
                    : "Please try again in a moment."
                }}
              </div>
            </div>
          </div>

          <!-- =================================================
               NO RESULTS
          ================================================== -->
          <div
            v-if="
              hasSearched &&
              !loading &&
              !searchError &&
              !hasMatch
            "
            class="state-message"
          >
            <div class="state-icon">
              <v-icon size="28">
                mdi-magnify-close
              </v-icon>
            </div>

            <div>
              <div class="state-title">
                {{
                  isFrench
                    ? "Aucune réponse correspondante"
                    : "No matching answer found"
                }}
              </div>

              <div class="state-description">
                {{
                  isFrench
                    ? "Essayez une expression plus courte ou utilisez d'autres mots."
                    : "Try a shorter phrase or use different words."
                }}
              </div>
            </div>
          </div>

          <!-- =================================================
               SEARCH RESULTS
          ================================================== -->
          <div
            v-if="
              hasMatch &&
              bestMatch &&
              !loading
            "
            class="search-results"
          >
            <!-- BEST MATCH -->
            <div class="result-section-heading">
              <div class="result-heading-left">
                <v-icon size="18">
                  mdi-check-decagram-outline
                </v-icon>

                <span>
                  {{
                    isFrench
                      ? "Meilleure correspondance"
                      : "Best match"
                  }}
                </span>
              </div>

              <span
                v-if="bestMatch.relevancePercentage"
                class="result-count"
              >
                {{ bestMatch.relevancePercentage }}%
                {{ isFrench ? "correspondance" : "match" }}
              </span>
            </div>

            <button
              type="button"
              class="best-result-card"
              @click="openAnswer(bestMatch)"
            >
              <div class="best-result-top">
                <div class="category-badge">
                  <v-icon size="15">
                    {{ bestMatch.midiicon || "mdi-folder-outline" }}
                  </v-icon>

                  <span>
                    {{ getCategory(bestMatch) }}
                  </span>
                </div>

                <v-icon
                  size="20"
                  class="result-arrow"
                >
                  mdi-arrow-top-right
                </v-icon>
              </div>

              <div class="best-result-question">
                <!-- <ForwardTypewriter
                  :key="`best-${searchVersion}-${bestMatch.id}`"
                  :strings="[getQuestion(bestMatch)]"
                  :loop="false"
                  :type-speed="32"
                  :delay="8"
                /> -->
                <ForwardTypewriter
                  :key="`best-${searchVersion}-${bestMatch.id}`"
                  :text="getQuestion(bestMatch)"
                  :speed="32"
                />
              </div>

              <div
                v-if="getShortDescription(bestMatch)"
                class="best-result-description"
              >
                {{ getShortDescription(bestMatch) }}
              </div>

              <div class="view-answer">
                <span>
                  {{ isFrench ? "Voir la réponse" : "View answer" }}
                </span>

                <v-icon size="17">
                  mdi-arrow-right
                </v-icon>
              </div>
            </button>

            <!-- RELATED QUESTIONS -->
            <div
              v-if="relatedQuestions.length"
              class="related-section"
            >
              <div class="related-title">
                <div>
                  <v-icon size="18">
                    mdi-link-variant
                  </v-icon>

                  <span>
                    {{
                      isFrench
                        ? "Questions connexes"
                        : "Related questions"
                    }}
                  </span>
                </div>

                <span class="related-count">
                  {{ relatedQuestions.length }}
                </span>
              </div>

              <div class="related-list">
                <button
                  v-for="(faq, index) in relatedQuestions"
                  :key="`${searchVersion}-${faq.id}`"
                  type="button"
                  class="related-item"
                  @click="openAnswer(faq)"
                >
                  <div class="related-number">
                    {{ String(index + 1).padStart(2, "0") }}
                  </div>

                  <div class="related-content">
                    <div class="related-question-text">
                      <!-- <ForwardTypewriter
                        :key="`related-${searchVersion}-${faq.id}`"
                        :strings="[getQuestion(faq)]"
                        :loop="false"
                        :type-speed="24"
                        :delay="5"
                      /> -->
                      <ForwardTypewriter
                        :key="`related-${searchVersion}-${faq.id}`"
                        :text="getQuestion(faq)"
                        :speed="24"
                      />
                    </div>

                    <div class="related-meta">
                      <span v-if="getCategory(faq)">
                        {{ getCategory(faq) }}
                      </span>

                      <span
                        v-if="faq.relevancePercentage"
                        class="related-score"
                      >
                        {{ faq.relevancePercentage }}%
                      </span>
                    </div>
                  </div>

                  <v-icon
                    size="19"
                    class="related-arrow"
                  >
                    mdi-chevron-right
                  </v-icon>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- =====================================================
             MESSAGE US TAB
        ====================================================== -->
        <div
          v-if="activeBox === 'B'"
          class="help-body message-body"
        >
          <div class="message-intro">
            <div class="message-intro-icon">
              <v-icon size="26">
                mdi-email-outline
              </v-icon>
            </div>

            <div>
              <div class="message-heading">
                {{
                  isFrench
                    ? "Envoyez-nous un message"
                    : "Send us a message"
                }}
              </div>

              <div class="message-description">
                {{
                  isFrench
                    ? "Vous n'avez pas trouvé ce que vous cherchiez ? Contactez notre équipe."
                    : "Didn't find what you were looking for? Send our team a message."
                }}
              </div>
            </div>
          </div>

          <div class="contact-form">
            <v-text-field
              v-model="name"
              :label="t(labelKey)"
              variant="outlined"
              density="comfortable"
              prepend-inner-icon="mdi-account-outline"
              autocomplete="name"
            />

            <v-text-field
              v-model="email"
              label="E-mail"
              type="email"
              variant="outlined"
              density="comfortable"
              prepend-inner-icon="mdi-email-outline"
              autocomplete="email"
            />

            <div class="message-editor-label">
              {{ isFrench ? "Message" : "Message" }}
            </div>

            <div class="editor-wrapper">
              <QuillEditor
                ref="quillRef"
                v-model="messageContent"
                theme="snow"
                content-type="html"
                @update:modelValue="val => messageContent = val"
              />
            </div>

            <v-file-input
              v-model="file"
              :label="t(uploadKey)"
              variant="outlined"
              density="comfortable"
              prepend-icon=""
              prepend-inner-icon="mdi-paperclip"
              show-size
              clearable
            />

            <div
              v-if="messageStatus"
              class="message-status"
              :class="messageStatusType"
            >
              <v-icon size="18">
                {{
                  messageStatusType === "success"
                    ? "mdi-check-circle-outline"
                    : "mdi-alert-circle-outline"
                }}
              </v-icon>

              {{ messageStatus }}
            </div>

            <div class="message-actions">
              <v-btn
                variant="flat"
                class="send-message-btn"
                :loading="sendingMessage"
                :disabled="sendingMessage"
                @click="handleSendEmail"
              >
                <v-icon
                  start
                  size="19"
                >
                  mdi-send-outline
                </v-icon>

                {{ t("send") }}
              </v-btn>
            </div>
          </div>
        </div>

        <!-- =====================================================
             FOOTER
        ====================================================== -->
        <div class="help-footer">
          <div class="footer-status">
            <span class="status-dot"></span>

            <span>
              {{
                activeBox === "A"
                  ? (
                    isFrench
                      ? "Réponses de notre centre d'aide"
                      : "Answers from our Help Center"
                  )
                  : (
                    isFrench
                      ? "Contactez notre équipe"
                      : "Contact our team"
                  )
              }}
            </span>
          </div>

          <div class="footer-brand">
            <v-icon size="14">
              mdi-shield-check-outline
            </v-icon>

            {{ isFrench ? "Assistance sécurisée" : "Secure support" }}
          </div>
        </div>
      </v-card>
    </v-dialog>

    
  </div>
  <!-- =========================================================
         ANSWER DETAIL DIALOG
         Main dialog remains mounted underneath.
    ========================================================== -->
    <v-dialog
      v-model="answerDialogVisible"
      width="680"
      max-width="calc(100vw - 32px)"
      scrollable
      style="z-index: 999999999"
    >
      <v-card class="answer-dialog-card">
        <!-- ANSWER HEADER -->
        <div class="answer-header">
          <div class="answer-header-content">
            <div class="answer-category-icon">
              <v-icon size="23">
                {{
                  selectedFaq?.midiicon ||
                  "mdi-frequently-asked-questions"
                }}
              </v-icon>
            </div>

            <div>
              <div class="answer-eyebrow">
                {{ getCategory(selectedFaq) }}
              </div>

              <div class="answer-header-label">
                {{
                  isFrench
                    ? "Centre d'aide"
                    : "Help Center"
                }}
              </div>
            </div>
          </div>

          <v-btn
            icon
            variant="text"
            size="small"
            class="answer-close-btn"
            @click="closeAnswer"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <!-- ANSWER CONTENT -->
        <v-card-text class="answer-content">
          <div
            v-if="selectedFaq?.relevancePercentage"
            class="answer-match-badge"
          >
            <v-icon size="15">
              mdi-check-decagram-outline
            </v-icon>

            {{ selectedFaq.relevancePercentage }}%
            {{ isFrench ? "correspondance" : "match" }}
          </div>

          <h2 class="answer-question">
            {{ getQuestion(selectedFaq) }}
          </h2>

          <div class="answer-divider"></div>

          <template
            v-if="
              selectedFaq &&
              selectedFaq.answers &&
              selectedFaq.answers.length
            "
          >
            <div
              v-for="(answer, index) in selectedFaq.answers"
              :key="answer.id || index"
              class="answer-block"
            >
              <div
                class="answer-html"
                v-html="sanitizeHtml(getAnswer(answer))"
              ></div>

              <a
                v-if="answer.anslink"
                :href="safeLink(answer.anslink)"
                class="answer-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span>
                  {{
                    answer.amslinktext ||
                    (isFrench ? "En savoir plus" : "Learn more")
                  }}
                </span>

                <v-icon size="17">
                  mdi-open-in-new
                </v-icon>
              </a>
            </div>
          </template>

          <div
            v-else
            class="no-answer"
          >
            <v-icon size="27">
              mdi-information-outline
            </v-icon>

            <span>
              {{
                isFrench
                  ? "Aucune réponse n'est actuellement disponible pour cette question."
                  : "There is currently no answer available for this question."
              }}
            </span>
          </div>
        </v-card-text>

        <!-- ANSWER FOOTER -->
        <div class="answer-footer">
          <div class="answer-source">
            <v-icon size="16">
              mdi-database-check-outline
            </v-icon>

            <span>
              {{
                isFrench
                  ? "Réponse du centre d'aide"
                  : "Help Center answer"
              }}
            </span>
          </div>

          <v-btn
            variant="flat"
            class="answer-done-btn"
            @click="closeAnswer"
          >
            <v-icon
              start
              size="18"
            >
              mdi-arrow-left
            </v-icon>

            {{
              isFrench
                ? "Retour aux résultats"
                : "Back to results"
            }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
</template>

<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch
} from "vue";

import { useI18n } from "vue-i18n";
import { QuillEditor } from "@vueup/vue-quill";
import "@vueup/vue-quill/dist/vue-quill.snow.css";

import DOMPurify from "dompurify";
import ForwardTypewriter from "@/components/ForwardTypewriter.vue";

import publicApi from "@/auth/publicApi";

// =============================================================
// I18N
// =============================================================

const { t, locale } = useI18n();

const labelKey = "name";
const uploadKey = "uploadfile";

const isFrench = computed(() => {
  const lang =
    locale.value ||
    localStorage.getItem("locale") ||
    "en";

  return String(lang)
    .toLowerCase()
    .startsWith("fr");
});

// =============================================================
// PROPS / EMITS
// =============================================================

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(["close"]);

// =============================================================
// MAIN DIALOG
// =============================================================

const localVisible = ref(props.visible);
const dialog = ref(null);

const activeBox = ref("A");

// =============================================================
// RESPONSIVE DIALOG POSITION
// =============================================================

const DIALOG_WIDTH = 760;
const DIALOG_HEIGHT = 760;
const SCREEN_MARGIN = 20;

const position = ref({
  top: SCREEN_MARGIN,
  left: SCREEN_MARGIN
});

function calculateInitialPosition() {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  const actualWidth = Math.min(
    DIALOG_WIDTH,
    viewportWidth - 32
  );

  const actualHeight = Math.min(
    DIALOG_HEIGHT,
    viewportHeight - 32
  );

  position.value = {
    top: Math.max(
      SCREEN_MARGIN,
      viewportHeight - actualHeight - SCREEN_MARGIN
    ),

    left: Math.max(
      SCREEN_MARGIN,
      viewportWidth - actualWidth - SCREEN_MARGIN
    )
  };
}

watch(
  () => props.visible,
  async val => {
    localVisible.value = val;

    if (val) {
      calculateInitialPosition();

      await nextTick();
    }
  }
);

// =============================================================
// DRAGGING
// =============================================================

let offset = {
  x: 0,
  y: 0
};

let isDragging = false;
let dragStarted = false;

const dragThreshold = 5;

let startMousePos = {
  x: 0,
  y: 0
};

function startDrag(e) {
  if (!dialog.value) return;

  // Disable dragging on mobile/tablet-size screens.
  if (window.innerWidth <= 768) return;

  startMousePos = {
    x: e.clientX,
    y: e.clientY
  };

  dragStarted = false;

  const element =
    dialog.value.$el ||
    dialog.value;

  const rect =
    element.getBoundingClientRect();

  offset = {
    x: e.clientX - rect.left,
    y: e.clientY - rect.top
  };

  document.addEventListener(
    "mousemove",
    checkDrag
  );

  document.addEventListener(
    "mouseup",
    stopDrag
  );
}

function checkDrag(e) {
  const dx = Math.abs(
    e.clientX - startMousePos.x
  );

  const dy = Math.abs(
    e.clientY - startMousePos.y
  );

  if (
    !dragStarted &&
    (dx > dragThreshold || dy > dragThreshold)
  ) {
    dragStarted = true;
    isDragging = true;
  }

  if (!isDragging) return;

  const element =
    dialog.value?.$el ||
    dialog.value;

  if (!element) return;

  const rect =
    element.getBoundingClientRect();

  const maxLeft =
    window.innerWidth -
    rect.width -
    SCREEN_MARGIN;

  const maxTop =
    window.innerHeight -
    80;

  position.value.left = Math.min(
    Math.max(
      SCREEN_MARGIN,
      e.clientX - offset.x
    ),
    Math.max(SCREEN_MARGIN, maxLeft)
  );

  position.value.top = Math.min(
    Math.max(
      SCREEN_MARGIN,
      e.clientY - offset.y
    ),
    Math.max(SCREEN_MARGIN, maxTop)
  );
}

function stopDrag() {
  isDragging = false;
  dragStarted = false;

  document.removeEventListener(
    "mousemove",
    checkDrag
  );

  document.removeEventListener(
    "mouseup",
    stopDrag
  );
}

// =============================================================
// CLOSE
// =============================================================

function close() {
  answerDialogVisible.value = false;
  localVisible.value = false;

  emit("close");
}

// =============================================================
// FAQ SMART SEARCH
// =============================================================

const input = ref("");

const loading = ref(false);
const hasSearched = ref(false);
const searchError = ref(false);

const bestMatch = ref(null);
const relatedQuestions = ref([]);

const searchVersion = ref(0);

const hasMatch = computed(() => {
  return !!bestMatch.value;
});

async function performSearch() {
  const search =
    String(input.value || "").trim();

  if (!search || loading.value) {
    return;
  }

  loading.value = true;
  searchError.value = false;
  hasSearched.value = true;

  bestMatch.value = null;
  relatedQuestions.value = [];

  try {
    const { data } =
      await publicApi.get(
        "/FQ/smart-search",
        {
          params: {
            search,
            maxResults: 8
          }
        }
      );

    bestMatch.value =
      data?.bestMatch || null;

    relatedQuestions.value =
      Array.isArray(data?.relatedQuestions)
        ? data.relatedQuestions
        : [];

    searchVersion.value++;
  } catch (error) {
    console.error(
      "FAQ smart search failed:",
      error.response?.data ||
      error.message
    );

    searchError.value = true;
  } finally {
    loading.value = false;
  }
}

function clearSearch() {
  input.value = "";

  hasSearched.value = false;
  searchError.value = false;

  bestMatch.value = null;
  relatedQuestions.value = [];
}

// =============================================================
// LANGUAGE HELPERS
// =============================================================

function getQuestion(faq) {
  if (!faq) return "";

  if (
    isFrench.value &&
    faq.fqquestion_Fr
  ) {
    return faq.fqquestion_Fr;
  }

  return faq.fqquestion || "";
}

function getCategory(faq) {
  if (!faq) return "";

  if (
    isFrench.value &&
    faq.category_Fr
  ) {
    return faq.category_Fr;
  }

  return faq.category || "";
}

function getShortDescription(faq) {
  if (!faq) return "";

  if (
    isFrench.value &&
    faq.shortdesc_Fr
  ) {
    return faq.shortdesc_Fr;
  }

  return faq.shortdesc || "";
}

function getAnswer(answer) {
  if (!answer) return "";

  if (
    isFrench.value &&
    answer.fqanswer_Fr
  ) {
    return answer.fqanswer_Fr;
  }

  return answer.fqanswer || "";
}

// =============================================================
// ANSWER DETAIL DIALOG
// =============================================================

const answerDialogVisible = ref(false);
const selectedFaq = ref(null);

function openAnswer(faq) {
  if (!faq) return;

  selectedFaq.value = faq;
  answerDialogVisible.value = true;
}

function closeAnswer() {
  answerDialogVisible.value = false;

  // Do not clear immediately.
  // This prevents content disappearing during close animation.
  window.setTimeout(() => {
    if (!answerDialogVisible.value) {
      selectedFaq.value = null;
    }
  }, 250);
}

// =============================================================
// HTML SANITIZATION
// =============================================================

function sanitizeHtml(html) {
  if (!html) return "";

  return DOMPurify.sanitize(
    html,
    {
      ALLOWED_TAGS: [
        "p",
        "br",
        "strong",
        "b",
        "em",
        "i",
        "u",
        "ul",
        "ol",
        "li",
        "a",
        "h1",
        "h2",
        "h3",
        "h4",
        "blockquote",
        "span"
      ],

      ALLOWED_ATTR: [
        "href",
        "target",
        "rel"
      ]
    }
  );
}

function safeLink(link) {
  if (!link) return "#";

  const value =
    String(link).trim();

  // Internal relative links.
  if (
    value.startsWith("/") &&
    !value.startsWith("//")
  ) {
    return value;
  }

  try {
    const parsed =
      new URL(value);

    if (
      parsed.protocol === "http:" ||
      parsed.protocol === "https:"
    ) {
      return parsed.href;
    }
  } catch {
    return "#";
  }

  return "#";
}

// =============================================================
// COPY
// =============================================================

async function copyCurrentContent() {
  let text = "";

  if (
    activeBox.value === "A" &&
    bestMatch.value
  ) {
    text +=
      `${getQuestion(bestMatch.value)}\n\n`;

    const answers =
      bestMatch.value.answers || [];

    answers.forEach(answer => {
      const html =
        getAnswer(answer);

      const plain =
        htmlToPlainText(html);

      text += `${plain}\n\n`;
    });

    if (relatedQuestions.value.length) {
      text += `${
        isFrench.value
          ? "Questions connexes"
          : "Related questions"
      }:\n`;

      relatedQuestions.value.forEach(
        faq => {
          text +=
            `- ${getQuestion(faq)}\n`;
        }
      );
    }
  }

  if (!text.trim()) {
    text =
      isFrench.value
        ? "Aucun résultat à copier."
        : "No search result to copy.";
  }

  try {
    await navigator.clipboard.writeText(
      text.trim()
    );
  } catch (error) {
    console.error(
      "Copy failed:",
      error
    );
  }
}

function htmlToPlainText(html) {
  if (!html) return "";

  const element =
    document.createElement("div");

  element.innerHTML =
    sanitizeHtml(html);

  return (
    element.textContent ||
    element.innerText ||
    ""
  );
}

// =============================================================
// PRINT
// =============================================================

function printCurrentContent() {
  if (!bestMatch.value) return;

  const question =
    getQuestion(bestMatch.value);

  const category =
    getCategory(bestMatch.value);

  const answers =
    bestMatch.value.answers || [];

  const answerHtml =
    answers
      .map(answer =>
        sanitizeHtml(
          getAnswer(answer)
        )
      )
      .join("<hr>");

  const printWindow =
    window.open(
      "",
      "",
      "width=850,height=700"
    );

  if (!printWindow) return;

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>${escapeHtml(question)}</title>

        <style>
          body {
            font-family:
              Arial,
              Helvetica,
              sans-serif;

            max-width: 760px;
            margin: 40px auto;
            padding: 0 24px;
            color: #1f2937;
            line-height: 1.65;
          }

          .category {
            font-size: 13px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: .05em;
            color: #64748b;
            margin-bottom: 12px;
          }

          h1 {
            font-size: 27px;
            line-height: 1.3;
            margin-bottom: 28px;
          }

          .answer {
            font-size: 16px;
          }
        </style>
      </head>

      <body>
        <div class="category">
          ${escapeHtml(category)}
        </div>

        <h1>
          ${escapeHtml(question)}
        </h1>

        <div class="answer">
          ${answerHtml}
        </div>
      </body>
    </html>
  `);

  printWindow.document.close();
  printWindow.focus();
  printWindow.print();
}

function escapeHtml(value) {
  return String(value || "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

// =============================================================
// MESSAGE US
// =============================================================

const quillRef = ref(null);

const name = ref("");
const email = ref("");
const messageContent = ref("");
const file = ref(null);

const sendingMessage = ref(false);

const messageStatus = ref("");
const messageStatusType = ref("");

async function handleSendEmail() {
  messageStatus.value = "";
  messageStatusType.value = "";

  const quill =
    quillRef.value?.getQuill?.();

  if (quill) {
    messageContent.value =
      quill.root.innerHTML;
  }

  if (
    !name.value.trim() ||
    !email.value.trim() ||
    !messageContent.value.trim()
  ) {
    messageStatusType.value =
      "error";

    messageStatus.value =
      isFrench.value
        ? "Veuillez remplir votre nom, votre e-mail et votre message."
        : "Please provide your name, email, and message.";

    return;
  }

  const formData =
    new FormData();

  formData.append(
    "name",
    name.value.trim()
  );

  formData.append(
    "email",
    email.value.trim()
  );

  formData.append(
    "telephone",
    "000-000-0000"
  );

  formData.append(
    "message",
    messageContent.value
  );

  formData.append(
    "consenttosentmsg",
    false
  );

  formData.append(
    "filtertext",
    "contactusmsg"
  );

  if (file.value) {
    const selectedFile =
      Array.isArray(file.value)
        ? file.value[0]
        : file.value;

    if (selectedFile) {
      formData.append(
        "file",
        selectedFile
      );
    }
  }

  sendingMessage.value = true;

  try {
    await publicApi.post(
      "/receiveusermessage",
      formData,
      {
        headers: {
          "Content-Type":
            "multipart/form-data"
        }
      }
    );

    messageStatusType.value =
      "success";

    messageStatus.value =
      isFrench.value
        ? "Votre message a été envoyé."
        : "Your message has been sent.";

    name.value = "";
    email.value = "";
    messageContent.value = "";
    file.value = null;

    if (quill) {
      quill.setText("");
    }
  } catch (error) {
    console.error(
      "Message submission failed:",
      error.response?.data ||
      error.message
    );

    messageStatusType.value =
      "error";

    messageStatus.value =
      isFrench.value
        ? "Le message n'a pas pu être envoyé. Veuillez réessayer."
        : "The message could not be sent. Please try again.";
  } finally {
    sendingMessage.value = false;
  }
}

// =============================================================
// RESIZE
// =============================================================

function updatePosition() {
  if (window.innerWidth <= 768) {
    position.value = {
      top: 8,
      left: 8
    };

    return;
  }

  calculateInitialPosition();
}

// =============================================================
// LIFECYCLE
// =============================================================

onMounted(() => {
  calculateInitialPosition();

  window.addEventListener(
    "resize",
    updatePosition
  );
});

onBeforeUnmount(() => {
  window.removeEventListener(
    "resize",
    updatePosition
  );

  document.removeEventListener(
    "mousemove",
    checkDrag
  );

  document.removeEventListener(
    "mouseup",
    stopDrag
  );
});
</script>

<style scoped>
/* ============================================================
   MAIN DIALOG
============================================================ */

.help-center-dialog {
  margin: 0 !important;
}

.help-center-card {
  height: min(760px, calc(100vh - 32px));
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 12px !important;
  border: 1px solid rgba(15, 23, 42, 0.10);
  background: #ffffff;
  box-shadow:
    0 24px 70px rgba(15, 23, 42, 0.18),
    0 8px 24px rgba(15, 23, 42, 0.10) !important;
}

/* ============================================================
   HEADER
============================================================ */

.help-header {
  min-height: 78px;
  padding: 15px 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-shrink: 0;
  color: #ffffff;
  background:
    linear-gradient(
      135deg,
      #000033 0%,
      #071b55 55%,
      #123c82 100%
    );
}

.cursor-move {
  cursor: move;
}

.help-brand {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.help-brand-icon {
  width: 44px;
  height: 44px;
  flex: 0 0 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 9px;
  background: rgba(255, 255, 255, 0.13);
  border: 1px solid rgba(255, 255, 255, 0.16);
}

.help-title {
  font-size: 17px;
  line-height: 1.25;
  font-weight: 700;
  letter-spacing: 0.01em;
}

.help-subtitle {
  margin-top: 3px;
  font-size: 12px;
  line-height: 1.4;
  color: rgba(255, 255, 255, 0.74);
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 2px;
}

.header-btn {
  color: rgba(255, 255, 255, 0.82) !important;
}

.header-btn:hover {
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.10);
}

/* ============================================================
   TABS
============================================================ */

.help-tabs {
  min-height: 58px;
  padding: 10px 18px 0;
  display: flex;
  align-items: flex-end;
  gap: 5px;
  flex-shrink: 0;
  background: #ffffff;
  border-bottom: 1px solid #e8edf4;
}

.help-tab {
  position: relative;
  min-height: 47px;
  padding: 0 17px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 0;
  border-radius: 7px 7px 0 0;
  background: transparent;
  color: #64748b;
  font-size: 13px;
  font-weight: 650;
  cursor: pointer;
  transition: 0.2s ease;
}

.help-tab:hover {
  color: #0f2d67;
  background: #f7f9fc;
}

.help-tab.active {
  color: #082b67;
  background: #f4f7fb;
}

.help-tab.active::after {
  content: "";
  position: absolute;
  left: 14px;
  right: 14px;
  bottom: -1px;
  height: 3px;
  border-radius: 3px 3px 0 0;
  background: #174a9c;
}

/* ============================================================
   BODY
============================================================ */

.help-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  background: #f8fafc;
}

.smart-help-body {
  padding: 20px;
}

/* ============================================================
   SEARCH
============================================================ */

.search-area {
  padding: 21px;
  border: 1px solid #e1e7ef;
  border-radius: 10px;
  background: #ffffff;
}

.search-heading {
  color: #172033;
  font-size: 20px;
  line-height: 1.3;
  font-weight: 750;
}

.search-description {
  margin-top: 5px;
  margin-bottom: 16px;
  color: #667085;
  font-size: 13px;
  line-height: 1.55;
}

.smart-search {
  width: 100%;
}

.smart-search :deep(.v-field) {
  border-radius: 8px !important;
  background: #ffffff;
}

.smart-search :deep(.v-field__input) {
  min-height: 52px;
  font-size: 14px;
}

.search-submit-btn {
  background: #0b3d82 !important;
  color: #ffffff !important;
}

.search-hint {
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
  color: #8490a3;
  font-size: 11px;
}

/* ============================================================
   INITIAL / LOADING / EMPTY
============================================================ */

.initial-state {
  min-height: 260px;
  padding: 45px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.initial-icon {
  width: 66px;
  height: 66px;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: #174a9c;
  background: #eaf1fb;
}

.initial-title {
  color: #1f2937;
  font-size: 16px;
  font-weight: 700;
}

.initial-text {
  max-width: 410px;
  margin-top: 7px;
  color: #778195;
  font-size: 13px;
  line-height: 1.55;
}

.search-loading {
  min-height: 220px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 13px;
  color: #667085;
  font-size: 13px;
}

.state-message {
  margin-top: 16px;
  padding: 18px;
  display: flex;
  align-items: flex-start;
  gap: 13px;
  border: 1px solid #e3e8ef;
  border-radius: 9px;
  background: #ffffff;
}

.state-icon {
  color: #64748b;
}

.state-title {
  color: #273244;
  font-size: 14px;
  font-weight: 700;
}

.state-description {
  margin-top: 3px;
  color: #7a8496;
  font-size: 12px;
  line-height: 1.5;
}

.error-state {
  border-color: #f0cccc;
  background: #fffafa;
}

/* ============================================================
   RESULTS
============================================================ */

.search-results {
  margin-top: 20px;
}

.result-section-heading {
  margin-bottom: 9px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.result-heading-left {
  display: flex;
  align-items: center;
  gap: 7px;
  color: #354054;
  font-size: 12px;
  font-weight: 750;
  text-transform: uppercase;
  letter-spacing: 0.055em;
}

.result-count {
  padding: 4px 8px;
  border-radius: 4px;
  background: #e9f7ef;
  color: #247348;
  font-size: 10px;
  font-weight: 700;
}

.best-result-card {
  width: 100%;
  padding: 18px;
  border: 1px solid #dbe4ef;
  border-radius: 9px;
  background: #ffffff;
  text-align: left;
  cursor: pointer;
  transition:
    border-color 0.18s ease,
    box-shadow 0.18s ease,
    transform 0.18s ease;
}

.best-result-card:hover {
  border-color: #a9bfdf;
  box-shadow: 0 8px 22px rgba(15, 45, 92, 0.08);
  transform: translateY(-1px);
}

.best-result-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.category-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #49617e;
  font-size: 11px;
  font-weight: 650;
}

.result-arrow {
  color: #9aa5b5;
}

.best-result-question {
  margin-top: 12px;
  color: #15233a;
  font-size: 18px;
  line-height: 1.45;
  font-weight: 750;
}

.best-result-description {
  margin-top: 7px;
  color: #68758a;
  font-size: 12px;
  line-height: 1.5;
}

.view-answer {
  margin-top: 14px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #174a9c;
  font-size: 12px;
  font-weight: 700;
}

/* ============================================================
   RELATED QUESTIONS
============================================================ */

.related-section {
  margin-top: 22px;
}

.related-title {
  margin-bottom: 9px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.related-title > div {
  display: flex;
  align-items: center;
  gap: 7px;
  color: #354054;
  font-size: 12px;
  font-weight: 750;
  text-transform: uppercase;
  letter-spacing: 0.055em;
}

.related-count {
  min-width: 23px;
  height: 23px;
  padding: 0 7px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  color: #5f6d80;
  background: #e9edf3;
  font-size: 10px;
  font-weight: 700;
}

.related-list {
  overflow: hidden;
  border: 1px solid #e1e6ed;
  border-radius: 9px;
  background: #ffffff;
}

.related-item {
  width: 100%;
  min-height: 68px;
  padding: 12px 14px;
  display: flex;
  align-items: center;
  gap: 12px;
  border: 0;
  border-bottom: 1px solid #edf0f4;
  background: #ffffff;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s ease;
}

.related-item:last-child {
  border-bottom: 0;
}

.related-item:hover {
  background: #f7f9fc;
}

.related-number {
  width: 27px;
  height: 27px;
  flex: 0 0 27px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  color: #61718a;
  background: #eef2f7;
  font-size: 9px;
  font-weight: 750;
}

.related-content {
  flex: 1;
  min-width: 0;
}

.related-question-text {
  color: #27354b;
  font-size: 13px;
  line-height: 1.4;
  font-weight: 650;
}

.related-meta {
  margin-top: 5px;
  display: flex;
  align-items: center;
  gap: 9px;
  color: #8a94a4;
  font-size: 10px;
}

.related-score {
  color: #3f765b;
}

.related-arrow {
  color: #9da7b5;
}

/* ============================================================
   MESSAGE US
============================================================ */

.message-body {
  padding: 20px;
}

.message-intro {
  margin-bottom: 18px;
  padding: 17px;
  display: flex;
  align-items: center;
  gap: 13px;
  border: 1px solid #e1e7ef;
  border-radius: 9px;
  background: #ffffff;
}

.message-intro-icon {
  width: 46px;
  height: 46px;
  flex: 0 0 46px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  color: #174a9c;
  background: #eaf1fb;
}

.message-heading {
  color: #273244;
  font-size: 15px;
  font-weight: 750;
}

.message-description {
  margin-top: 3px;
  color: #778195;
  font-size: 12px;
}

.contact-form {
  padding: 20px;
  border: 1px solid #e1e7ef;
  border-radius: 9px;
  background: #ffffff;
}

.message-editor-label {
  margin-bottom: 7px;
  color: #4a5568;
  font-size: 12px;
  font-weight: 650;
}

.editor-wrapper {
  height: 210px;
  margin-bottom: 24px;
}

.editor-wrapper :deep(.ql-toolbar) {
  border-radius: 6px 6px 0 0;
  border-color: #cfd6df;
}

.editor-wrapper :deep(.ql-container) {
  height: 155px;
  border-radius: 0 0 6px 6px;
  border-color: #cfd6df;
}

.message-actions {
  display: flex;
  justify-content: flex-end;
}

.send-message-btn {
  min-height: 42px;
  padding: 0 20px !important;
  border-radius: 6px !important;
  background: #0b3d82 !important;
  color: #ffffff !important;
  text-transform: none !important;
  font-weight: 650;
}

.message-status {
  margin-bottom: 14px;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  gap: 7px;
  border-radius: 6px;
  font-size: 12px;
}

.message-status.success {
  background: #edf8f1;
  color: #246b44;
}

.message-status.error {
  background: #fff1f1;
  color: #a23838;
}

/* ============================================================
   MAIN FOOTER
============================================================ */

.help-footer {
  min-height: 44px;
  padding: 0 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
  flex-shrink: 0;
  border-top: 1px solid #e5e9ef;
  background: #ffffff;
  color: #8a94a4;
  font-size: 10px;
}

.footer-status,
.footer-brand {
  display: flex;
  align-items: center;
  gap: 6px;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #39a96b;
}

/* ============================================================
   ANSWER DIALOG
============================================================ */

.answer-dialog-card {
  max-height: min(720px, calc(100vh - 40px));
  overflow: hidden;
  border-radius: 11px !important;
  border: 1px solid rgba(15, 23, 42, 0.10);
  box-shadow:
    0 30px 80px rgba(15, 23, 42, 0.25) !important;
}

.answer-header {
  min-height: 76px;
  padding: 14px 17px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
  flex-shrink: 0;
  color: #ffffff;
  background:
    linear-gradient(
      135deg,
      #000033 0%,
      #0b2c68 100%
    );
}

.answer-header-content {
  display: flex;
  align-items: center;
  gap: 11px;
}

.answer-category-icon {
  width: 43px;
  height: 43px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.13);
}

.answer-eyebrow {
  color: rgba(255, 255, 255, 0.72);
  font-size: 10px;
  font-weight: 650;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.answer-header-label {
  margin-top: 2px;
  font-size: 14px;
  font-weight: 700;
}

.answer-close-btn {
  color: #ffffff !important;
}

.answer-content {
  padding: 27px !important;
  overflow-y: auto;
  background: #ffffff;
}

.answer-match-badge {
  width: fit-content;
  margin-bottom: 13px;
  padding: 5px 9px;
  display: flex;
  align-items: center;
  gap: 5px;
  border-radius: 5px;
  color: #2d704d;
  background: #eaf7ef;
  font-size: 10px;
  font-weight: 700;
}

.answer-question {
  margin: 0;
  color: #172033;
  font-size: 23px;
  line-height: 1.4;
  font-weight: 750;
}

.answer-divider {
  width: 42px;
  height: 3px;
  margin: 19px 0 22px;
  border-radius: 2px;
  background: #174a9c;
}

.answer-block + .answer-block {
  margin-top: 22px;
  padding-top: 22px;
  border-top: 1px solid #edf0f4;
}

.answer-html {
  color: #455267;
  font-size: 14px;
  line-height: 1.8;
}

.answer-html :deep(p) {
  margin: 0 0 13px;
}

.answer-html :deep(ul),
.answer-html :deep(ol) {
  margin: 10px 0;
  padding-left: 22px;
}

.answer-html :deep(a) {
  color: #174a9c;
}

.answer-link {
  width: fit-content;
  margin-top: 18px;
  padding: 8px 11px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #d5deeb;
  border-radius: 5px;
  color: #174a9c;
  background: #f8fafc;
  font-size: 11px;
  font-weight: 700;
  text-decoration: none;
}

.answer-link:hover {
  border-color: #aac0df;
  background: #f2f6fb;
}

.no-answer {
  padding: 20px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  border-radius: 7px;
  background: #f8fafc;
  color: #6f7b8e;
  font-size: 13px;
}

.answer-footer {
  min-height: 64px;
  padding: 10px 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
  flex-shrink: 0;
  border-top: 1px solid #e7ebf0;
  background: #f9fafb;
}

.answer-source {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #7b8697;
  font-size: 10px;
}

.answer-done-btn {
  min-height: 38px;
  padding: 0 15px !important;
  border-radius: 5px !important;
  background: #0b3d82 !important;
  color: #ffffff !important;
  text-transform: none !important;
  font-size: 11px;
  font-weight: 650;
}

/* ============================================================
   MOBILE
============================================================ */

@media (max-width: 768px) {
  .help-center-dialog {
    top: 8px !important;
    left: 8px !important;
  }

  .help-center-card {
    width: calc(100vw - 16px);
    height: calc(100vh - 16px);
    border-radius: 9px !important;
  }

  .cursor-move {
    cursor: default;
  }

  .help-header {
    min-height: 70px;
    padding: 12px 13px;
  }

  .help-brand-icon {
    width: 38px;
    height: 38px;
    flex-basis: 38px;
  }

  .help-subtitle {
    display: none;
  }

  .header-actions {
    gap: 0;
  }

  .help-tabs {
    padding-left: 10px;
    padding-right: 10px;
  }

  .help-tab {
    flex: 1;
    justify-content: center;
    padding: 0 8px;
  }

  .smart-help-body,
  .message-body {
    padding: 12px;
  }

  .search-area {
    padding: 16px;
  }

  .search-heading {
    font-size: 18px;
  }

  .search-hint {
    display: none;
  }

  .best-result-question {
    font-size: 16px;
  }

  .help-footer {
    padding: 0 12px;
  }

  .footer-brand {
    display: none;
  }

  .answer-dialog-card {
    max-height: calc(100vh - 20px);
  }

  .answer-content {
    padding: 20px !important;
  }

  .answer-question {
    font-size: 20px;
  }

  .answer-footer {
    align-items: stretch;
    flex-direction: column;
  }

  .answer-source {
    display: none;
  }

  .answer-done-btn {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .header-actions .header-btn:nth-child(1),
  .header-actions .header-btn:nth-child(2) {
    display: none;
  }

  .help-title {
    font-size: 15px;
  }

  .result-section-heading {
    align-items: flex-start;
  }

  .best-result-card {
    padding: 15px;
  }

  .related-number {
    display: none;
  }
}
</style>